/**
 * Utility functions for transforming device names and extracting farm information
 */

/**
 * Extract farm name from device label and format as "Farm Name [UUID]"
 *
 * Transforms device names like:
 * "Modberry 500 CM4 [04db4394] - MTS J. Oenema"
 * to:
 * "MTS J. Oenema [04db4394]"
 *
 * @param deviceLabel - The full device label/name
 * @returns Formatted farm name with UUID in brackets
 */
export function extractFormattedFarmName(deviceLabel: string): string {
  // Extract farm name and UUID from labels like "Modberry 500 CM4 [04db4394] - MTS J. Oenema"
  const fullMatch = deviceLabel.match(/\[([a-f0-9]+)\]\s*-\s*(.+)$/);
  if (fullMatch) {
    const uuid = fullMatch[1];
    const farmName = fullMatch[2].trim();
    return `${farmName} [${uuid}]`;
  }

  // Fallback: try to extract just the farm name after the dash
  const fallbackMatch = deviceLabel.match(/- (.+)$/);
  return fallbackMatch ? fallbackMatch[1].trim() : deviceLabel;
}

/**
 * Extract just the farm name without UUID formatting (for compatibility)
 *
 * @param deviceLabel - The full device label/name
 * @returns Farm name without UUID
 */
export function extractFarmNameOnly(deviceLabel: string): string {
  // Extract farm name from labels like "Modberry 500 CM4 [04db4394] - MTS J. Oenema"
  const match = deviceLabel.match(/- (.+)$/);
  return match ? match[1].trim() : deviceLabel;
}

/**
 * Extract UUID from device label
 *
 * @param deviceLabel - The full device label/name
 * @returns UUID string or null if not found
 */
/**
 * Compact device name: strip Modberry prefix and reorder to "Farm Name [hash]".
 * "Modberry 500 CM4 [04db4394] - MTS J. Oenema" → "MTS J. Oenema [04db4394]"
 * Falls back to prefix-stripping if the pattern doesn't match.
 */
export function shortDeviceName(deviceLabel: string): string {
  const match = deviceLabel.match(/\[([a-f0-9]+)\]\s*[-:]\s*(.+)$/i);
  if (match) {
    return `${match[2].trim()} [${match[1]}]`;
  }
  return deviceLabel.replace(/^Modberry\s+(?:500\s+CM4|CM4\s+500)\s+/i, '');
}

export function extractDeviceUUID(deviceLabel: string): string | null {
  const match = deviceLabel.match(/\[([a-f0-9]+)\]/i); // Case-insensitive flag
  return match ? match[1].toLowerCase() : null; // Normalize to lowercase
}